import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material'
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Key as KeyIcon,
  DevicesOther as DevicesIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material'
import apiClient from '../services/api'
import toast from 'react-hot-toast'

interface MFAMethod {
  id: string
  type: 'totp' | 'sms' | 'email'
  display: string
  is_primary: boolean
  last_used?: string
}

interface TrustedDevice {
  id: string
  device_label?: string
  ip_address?: string
  created_at: string
  trusted_until: string
  last_used_at?: string
}

interface MFAStatus {
  mfa_enabled: boolean
  mfa_required: boolean
  methods_configured: MFAMethod[]
  tenant_mfa_status: 'disabled' | 'optional' | 'required'
  can_configure_mfa: boolean
  backup_codes_available: number
  trusted_devices: TrustedDevice[]
}

const MFASettings: React.FC = () => {
  const [status, setStatus] = useState<MFAStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [setupDialog, setSetupDialog] = useState(false)
  const [setupStep, setSetupStep] = useState(0)
  const [selectedMethod, setSelectedMethod] = useState<'totp' | 'sms' | 'email'>('totp')
  const [verificationCode, setVerificationCode] = useState('')
  const [methodId, setMethodId] = useState('')
  const [qrCode, setQrCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [userPhone, setUserPhone] = useState<string | null>(null)

  useEffect(() => {
    loadStatus()
  }, [])

  const loadStatus = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get<MFAStatus>('/mfa/status')
      setStatus(response.data)
      
      // Încarcă și user phone pentru verificare SMS
      const userResponse = await apiClient.get('/auth/me')
      setUserPhone(userResponse.data.phone || null)
    } catch (error: any) {
      console.error('Error loading MFA status:', error)
      toast.error('Nu s-au putut încărca setările MFA')
    } finally {
      setLoading(false)
    }
  }

  const handleStartSetup = () => {
    setSetupDialog(true)
    setSetupStep(0)
    setVerificationCode('')
    setQrCode('')
    setBackupCodes([])
  }

  const handleMethodSelection = async () => {
    // Verificare pentru SMS - trebuie să existe număr în cont
    if (selectedMethod === 'sms' && !userPhone) {
      toast.error('Nu aveți număr de telefon în cont. Actualizați profilul mai întâi.')
      return
    }

    try {
      const payload: any = { method_type: selectedMethod }
      // Phone nu mai e trimis - backend folosește user.phone

      const response = await apiClient.post('/mfa/setup', payload)
      setMethodId(response.data.method_id)
      
      if (selectedMethod === 'totp') {
        setQrCode(response.data.qr_code)
      }
      
      setSetupStep(1)
      toast.success('Cod de verificare trimis!')
    } catch (error: any) {
      console.error('Error setting up MFA:', error)
      toast.error(error.response?.data?.detail || 'Eroare la configurarea MFA')
    }
  }

  const handleVerify = async () => {
    try {
      const response = await apiClient.post('/mfa/verify', {
        method_id: methodId,
        code: verificationCode,
      })

      if (response.data.success) {
        if (response.data.backup_codes) {
          setBackupCodes(response.data.backup_codes)
          setSetupStep(2)
        } else {
          toast.success('MFA activat cu succes!')
          setSetupDialog(false)
          loadStatus()
        }
      } else {
        toast.error(response.data.message || 'Cod invalid')
      }
    } catch (error: any) {
      console.error('Error verifying MFA:', error)
      toast.error(error.response?.data?.detail || 'Cod invalid')
    }
  }

  const handleDeleteMethod = async (methodId: string) => {
    if (!confirm('Sigur doriți să ștergeți această metodă MFA?')) return

    try {
      await apiClient.delete(`/mfa/methods/${methodId}`)
      toast.success('Metodă MFA ștearsă')
      loadStatus()
    } catch (error: any) {
      console.error('Error deleting MFA method:', error)
      toast.error(error.response?.data?.detail || 'Eroare la ștergerea metodei')
    }
  }

  const handleSetPrimary = async (methodId: string) => {
    try {
      await apiClient.patch(`/mfa/methods/${methodId}/set-primary`)
      toast.success('Metodă primară actualizată')
      loadStatus()
    } catch (error: any) {
      console.error('Error setting primary method:', error)
      toast.error(error.response?.data?.detail || 'Eroare la setarea metodei primare')
    }
  }

  const handleRevokeTrustedDevice = async (deviceId: string) => {
    if (!confirm('Sigur doriți să revocați acest dispozitiv?')) return

    try {
      await apiClient.delete(`/mfa/trusted-devices/${deviceId}`)
      toast.success('Dispozitiv revocat')
      loadStatus()
    } catch (error: any) {
      console.error('Error revoking device:', error)
      toast.error('Eroare la revocarea dispozitivului')
    }
  }

  const handleGenerateBackupCodes = async () => {
    try {
      const response = await apiClient.post('/mfa/recovery-codes/generate')
      setBackupCodes(response.data.codes)
      setSetupStep(2)
      setSetupDialog(true)
    } catch (error: any) {
      console.error('Error generating backup codes:', error)
      toast.error(error.response?.data?.detail || 'Eroare la generarea codurilor')
    }
  }

  const getMethodIcon = (type: string) => {
    switch (type) {
      case 'sms': return <PhoneIcon />
      case 'email': return <EmailIcon />
      case 'totp': return <KeyIcon />
      default: return <SecurityIcon />
    }
  }

  // Compact styles
  const inputSx = { '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } };
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { color: 'text.secondary', fontSize: '0.85rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    },
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
  }

  if (!status) {
    return <Alert severity="error" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>Nu s-au putut încărca setările MFA</Alert>
  }

  if (!status.can_configure_mfa) {
    return <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>MFA este dezactivat pentru organizația dumneavoastră</Alert>
  }

  return (
    <Box>
      {/* Status Overview */}
      <Card sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SecurityIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Autentificare Multi-Factor (MFA)</Typography>
            </Box>
            <Chip label={status.mfa_enabled ? 'Activat' : 'Dezactivat'} color={status.mfa_enabled ? 'success' : 'default'} size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
          </Box>

          {status.mfa_required && (
            <Alert severity="warning" sx={{ mb: 1, py: 0.25, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>MFA este obligatoriu pentru contul dumneavoastră</Alert>
          )}

          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, mt: 1, mb: 0.5 }}>Metode configurate ({status.methods_configured.length})</Typography>
          {status.methods_configured.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {status.methods_configured.map((method) => (
                <Box key={method.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ '& svg': { fontSize: 16 } }}>{getMethodIcon(method.type)}</Box>
                    <Typography sx={{ fontSize: '0.75rem' }}>{method.display}</Typography>
                    {method.is_primary && <Chip label="Principal" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} />}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    {!method.is_primary && <IconButton size="small" onClick={() => handleSetPrimary(method.id)} title="Setează principal" sx={{ p: 0.25 }}><StarBorderIcon sx={{ fontSize: 16 }} /></IconButton>}
                    <IconButton size="small" onClick={() => handleDeleteMethod(method.id)} disabled={status.mfa_required && status.methods_configured.length === 1} sx={{ p: 0.25 }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Nicio metodă configurată</Typography>
          )}

          <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
            <Button startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={handleStartSetup} variant="contained" size="small" sx={{ height: 28, fontSize: '0.72rem', textTransform: 'none' }}>Adaugă MFA</Button>
            {status.mfa_enabled && <Button onClick={handleGenerateBackupCodes} variant="outlined" size="small" sx={{ height: 28, fontSize: '0.72rem', textTransform: 'none' }}>Coduri rezervă</Button>}
          </Box>

          {status.backup_codes_available > 0 && (
            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.5 }}>Coduri de rezervă: {status.backup_codes_available}</Typography>
          )}
        </CardContent>
      </Card>

      {/* Trusted Devices */}
      <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <DevicesIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Dispozitive de încredere</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mb: 1 }}>Dispozitivele de încredere nu cer MFA 7 zile</Typography>

          {status.trusted_devices.length === 0 ? (
            <Alert severity="info" sx={{ py: 0.25, '& .MuiAlert-message': { fontSize: '0.72rem' } }}>Nu aveți dispozitive de încredere</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {status.trusted_devices.map((device) => (
                <Box key={device.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem' }}>{device.device_label || 'Dispozitiv necunoscut'}</Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>IP: {device.ip_address || 'N/A'} • {new Date(device.created_at).toLocaleDateString('ro-RO')}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRevokeTrustedDevice(device.id)} sx={{ p: 0.25 }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Setup Dialog - Compact & Consistent */}
      <Dialog open={setupDialog} onClose={() => setSetupDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>Configurare MFA</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Stepper activeStep={setupStep} sx={{ mb: 2, '& .MuiStepLabel-label': { fontSize: '0.75rem' } }}>
            <Step><StepLabel>Metodă</StepLabel></Step>
            <Step><StepLabel>Verificare</StepLabel></Step>
            <Step><StepLabel>Coduri</StepLabel></Step>
          </Stepper>

          {setupStep === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl fullWidth size="small" sx={inputSx}>
                <InputLabel>Metodă MFA</InputLabel>
                <Select value={selectedMethod} onChange={(e) => setSelectedMethod(e.target.value as any)} label="Metodă MFA">
                  <MenuItem value="totp" sx={{ fontSize: '0.8rem' }}>Aplicație (TOTP)</MenuItem>
                  <MenuItem value="sms" sx={{ fontSize: '0.8rem' }}>SMS</MenuItem>
                  <MenuItem value="email" sx={{ fontSize: '0.8rem' }}>Email</MenuItem>
                </Select>
              </FormControl>
              {selectedMethod === 'sms' && (userPhone ? <Alert severity="info" sx={{ py: 0.25, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>Cod pe: <strong>{userPhone}</strong></Alert> : <Alert severity="warning" sx={{ py: 0.25, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>Adăugați telefon în Profil</Alert>)}
              {selectedMethod === 'email' && <Alert severity="info" sx={{ py: 0.25, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>Cod pe email-ul contului</Alert>}
              {selectedMethod === 'totp' && <Alert severity="info" sx={{ py: 0.25, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>Scanați QR cu Google Authenticator</Alert>}
            </Box>
          )}

          {setupStep === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {qrCode && (
                <Box sx={{ textAlign: 'center' }}>
                  <img src={qrCode} alt="QR Code" style={{ maxWidth: '180px' }} />
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Scanați codul QR</Typography>
                </Box>
              )}
              <TextField fullWidth label="Cod verificare" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} placeholder="123456" helperText="Introduceți codul primit" size="small" sx={inputSx} />
            </Box>
          )}

          {setupStep === 2 && backupCodes.length > 0 && (
            <Box>
              <Alert severity="warning" sx={{ mb: 1, py: 0.25, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>Salvați aceste coduri! Fiecare poate fi folosit o dată.</Alert>
              <Box sx={{ p: 1.5, bgcolor: 'grey.900', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', color: 'common.white' }}>
                {backupCodes.map((code, idx) => (<Typography key={idx} sx={{ fontSize: '0.75rem' }}>{code}</Typography>))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {setupStep === 0 && (
            <>
              <Button onClick={() => setSetupDialog(false)} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Anulează</Button>
              <Button onClick={handleMethodSelection} variant="contained" disabled={selectedMethod === 'sms' && !userPhone} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Continuă</Button>
            </>
          )}
          {setupStep === 1 && (
            <>
              <Button onClick={() => setSetupStep(0)} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Înapoi</Button>
              <Button onClick={handleVerify} variant="contained" size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Verifică</Button>
            </>
          )}
          {setupStep === 2 && (
            <Button onClick={() => { setSetupDialog(false); loadStatus(); }} variant="contained" size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Am salvat codurile</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MFASettings

